'use client';

import React, { createContext, useState, useEffect, useCallback } from 'react';
import { authAPI, setAuthToken, clearAuthToken } from '../services/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const clearSession = useCallback(() => {
    clearAuthToken();
    localStorage.removeItem('user');
    setUser(null);
    setError(null);
  }, []);

  const validateSession = useCallback(async () => {
    try {
      const response = await authAPI.getMe();

      if (response?.data?.user) {
        const nextUser = response.data.user;
        localStorage.setItem('user', JSON.stringify(nextUser));
        setUser(nextUser);
        return true;
      }

      clearSession();
      return false;
    } catch (err) {
      clearSession();
      return false;
    }
  }, [clearSession]);

  useEffect(() => {
    const restoreSession = async () => {
      const savedUser = localStorage.getItem('user');

      if (savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
        } catch (err) {
          console.error('Error parsing saved user data:', err);
        }
      }

      await validateSession();
      setLoading(false);
    };

    restoreSession();
  }, [clearSession, validateSession]);

  const login = async (username, password) => {
    try {
      setError(null);
      const response = await authAPI.login(username, password);
      const { token, user } = response.data;

      setAuthToken(token);
      localStorage.setItem('user', JSON.stringify(user));

      setUser(user);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.message || 'Login failed';
      setError(message);
      throw err;
    }
  };

  const register = async (registerData) => {
    try {
      setError(null);
      const response = await authAPI.register(registerData);
      const { token, user } = response.data;

      setAuthToken(token);
      localStorage.setItem('user', JSON.stringify(user));

      setUser(user);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.message || 'Registration failed';
      setError(message);
      throw err;
    }
  };

  const logout = () => {
    authAPI.logout()
      .catch(() => {})
      .finally(() => {
        clearSession();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      });
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
